import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import TopUpModal from './TopUpModal';
import PlansModal from './PlansModal';

const Header = () => {
  const location = useLocation();
  const { isAuthenticated, user } = useUser();
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Function to determine if a nav link is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      {/* Account Locked Warning Banner */}
      {isAuthenticated && user && user.subscriptionStatus === 'locked' && (
        <div className="bg-red-500 text-white py-3 px-6 text-center">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-medium">
              ACCOUNT LOCKED DUE TO PAYMENT FAILURE - 
              <Link to="/account?tab=billing" className="underline ml-1 hover:text-white/90">
                Update payment method to restore access
              </Link>
            </span>
          </div>
        </div>
      )}
      
      <header className="bg-surface border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2">
          <div className="flex justify-between items-center">
          {/* Logo - Contemporary minimal design */}
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group">
            <img 
              src="/logo.webp" 
              alt="LoRA Tag Logo" 
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg shadow-button group-hover:shadow-lg transition-all duration-300"
            />
            <div>
              <span className="text-base sm:text-lg font-semibold text-primaryText">LoRA Tag</span>
              <span className="text-xs text-secondaryText block -mt-1 hidden sm:block">AI-Powered Training Data</span>
            </div>
          </Link>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-button transition-colors"
          >
            <svg className="w-6 h-6 text-primaryText" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-all duration-300 ${
                isActive('/') 
                  ? 'text-accent' 
                  : 'text-secondaryText hover:text-primaryText'
              }`}
            >
              Upload
            </Link>
            {isAuthenticated && (
              <Link 
                to="/history" 
                className={`text-sm font-medium transition-all duration-300 ${
                  isActive('/history') 
                    ? 'text-accent' 
                    : 'text-secondaryText hover:text-primaryText'
                }`}
              >
                History
              </Link>
            )}
            <Link 
              to="/blog" 
              className={`text-sm font-medium transition-all duration-300 ${
                isActive('/blog') 
                  ? 'text-accent' 
                  : 'text-secondaryText hover:text-primaryText'
              }`}
            >
              Blog
            </Link>
            <Link 
              to="/account" 
              className={`text-sm font-medium transition-all duration-300 ${
                isActive('/account') 
                  ? 'text-accent' 
                  : 'text-secondaryText hover:text-primaryText'
              }`}
            >
              {isAuthenticated ? 'Account' : 'Sign In'}
            </Link>
            
            {/* Admin link - only visible for admin users */}
            {user && user.subscriptionTier === 'admin' && (
              <>
                <Link 
                  to="/admin" 
                  className={`text-sm font-medium transition-all duration-300 ${
                    isActive('/admin') 
                      ? 'text-accent' 
                      : 'text-secondaryText hover:text-primaryText'
                  }`}
                >
                  Admin
                </Link>
                <Link 
                  to="/blog/admin" 
                  className={`text-sm font-medium transition-all duration-300 ${
                    location.pathname.startsWith('/blog/admin')
                      ? 'text-accent' 
                      : 'text-secondaryText hover:text-primaryText'
                  }`}
                >
                  Blog Admin
                </Link>
                <span className="text-xs text-accent font-medium px-2 py-0.5 bg-accent/10 rounded">
                  Admin • Unlimited
                </span>
              </>
            )}
            
            {/* Top Up Credits button for paid users, Upgrade for free users, Subscribe for cancelled */}
            {isAuthenticated && user && user.subscriptionTier !== 'admin' && (
              <>
                {user.subscriptionStatus === 'canceled' ? (
                  <button 
                    onClick={() => setShowPlansModal(true)}
                    className="bg-accent text-black hover:bg-accent/90 transition-all rounded-lg text-sm py-1.5 px-4 flex items-center gap-2 font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Renew Subscription
                  </button>
                ) : ['starter', 'professional', 'business'].includes(user.subscriptionTier) ? (
                  <button 
                    onClick={() => setShowTopUpModal(true)}
                    className="bg-accent text-black hover:bg-accent/90 transition-all rounded-lg text-sm py-1.5 px-4 flex items-center gap-2 font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Top Up
                  </button>
                ) : user.subscriptionTier === 'free' ? (
                  <Link 
                    to="/account?tab=billing"
                    className="bg-accent text-black hover:bg-accent/90 transition-all rounded-lg text-sm py-1.5 px-4 flex items-center gap-2 font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Upgrade
                  </Link>
                ) : null}
              </>
            )}
            
            {/* Sign in button for non-authenticated users */}
            {!isAuthenticated && (
              <Link to="/account?mode=signup" className="btn-primary text-sm py-1.5 px-4">
                Get Started
              </Link>
            )}
          </nav>
        </div>
      </div>
      
      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-surface border-b border-border">
          <nav className="px-4 py-4 space-y-3">
            <Link 
              to="/" 
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block py-2 text-sm font-medium transition-all duration-300 ${
                isActive('/') 
                  ? 'text-accent' 
                  : 'text-secondaryText hover:text-primaryText'
              }`}
            >
              Upload
            </Link>
            {isAuthenticated && (
              <Link 
                to="/history" 
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block py-2 text-sm font-medium transition-all duration-300 ${
                  isActive('/history') 
                    ? 'text-accent' 
                    : 'text-secondaryText hover:text-primaryText'
                }`}
              >
                History
              </Link>
            )}
            <Link 
              to="/blog" 
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block py-2 text-sm font-medium transition-all duration-300 ${
                isActive('/blog') 
                  ? 'text-accent' 
                  : 'text-secondaryText hover:text-primaryText'
              }`}
            >
              Blog
            </Link>
            <Link 
              to="/account" 
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block py-2 text-sm font-medium transition-all duration-300 ${
                isActive('/account') 
                  ? 'text-accent' 
                  : 'text-secondaryText hover:text-primaryText'
              }`}
            >
              {isAuthenticated ? 'Account' : 'Sign In'}
            </Link>
            
            {/* Admin link - only visible for admin users */}
            {user && user.subscriptionTier === 'admin' && (
              <>
                <Link 
                  to="/admin" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-2 text-sm font-medium transition-all duration-300 ${
                    isActive('/admin') 
                      ? 'text-accent' 
                      : 'text-secondaryText hover:text-primaryText'
                  }`}
                >
                  Admin
                </Link>
                <Link 
                  to="/blog/admin" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-2 text-sm font-medium transition-all duration-300 ${
                    location.pathname.startsWith('/blog/admin')
                      ? 'text-accent' 
                      : 'text-secondaryText hover:text-primaryText'
                  }`}
                >
                  Blog Admin
                </Link>
                <span className="block text-xs text-accent font-medium px-2 py-0.5 bg-accent/10 rounded w-fit">
                  Admin • Unlimited
                </span>
              </>
            )}
            
            {/* Mobile Action Buttons */}
            {isAuthenticated && user && user.subscriptionTier !== 'admin' && (
              <div className="pt-2 border-t border-border">
                {user.subscriptionStatus === 'canceled' ? (
                  <button 
                    onClick={() => {
                      setShowPlansModal(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full bg-accent text-black hover:bg-accent/90 transition-all rounded-lg text-sm py-2 px-4 flex items-center justify-center gap-2 font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Renew Subscription
                  </button>
                ) : ['starter', 'professional', 'business'].includes(user.subscriptionTier) ? (
                  <button 
                    onClick={() => {
                      setShowTopUpModal(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full bg-accent text-black hover:bg-accent/90 transition-all rounded-lg text-sm py-2 px-4 flex items-center justify-center gap-2 font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Top Up
                  </button>
                ) : user.subscriptionTier === 'free' ? (
                  <Link 
                    to="/account?tab=billing"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full bg-accent text-black hover:bg-accent/90 transition-all rounded-lg text-sm py-2 px-4 text-center font-medium"
                  >
                    <svg className="inline-block w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Upgrade
                  </Link>
                ) : null}
              </div>
            )}
            
            {/* Sign in button for non-authenticated users */}
            {!isAuthenticated && (
              <div className="pt-2">
                <Link 
                  to="/account?mode=signup" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full btn-primary text-sm py-2 px-4 text-center"
                >
                  Get Started
                </Link>
              </div>
            )}
          </nav>
        </div>
      )}
      
      {/* Top Up Modal */}
      <TopUpModal 
        isOpen={showTopUpModal} 
        onClose={() => setShowTopUpModal(false)}
        currentTier={user?.subscriptionTier || 'free'}
        currentUsage={{
          used: user?.usage?.currentPeriod?.imagesProcessed || 0,
          limit: user?.usage?.currentPeriod?.monthlyLimit || 10,
          remaining: (user?.usage?.currentPeriod?.monthlyLimit || 10) - (user?.usage?.currentPeriod?.imagesProcessed || 0)
        }}
      />
      
      {/* Plans Modal */}
      <PlansModal 
        isOpen={showPlansModal} 
        onClose={() => setShowPlansModal(false)}
        currentTier={user?.subscriptionStatus === 'canceled' ? 'none' : (user?.subscriptionTier || 'free')}
      />
    </header>
    </>
  );
};

export default Header;